import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Microscope, Route, ScanSearch, GitBranch, Users, ChevronRight } from "lucide-react";
import {
  HubSection,
  LoadingState,
  ErrorState,
  EmptyState,
  StrengthBar,
  AttentionBadge,
  beliefSentence,
  healthStatus,
} from "@/components/research-hub";
import { useCioThesisHealth } from "@/hooks/useResearchHubCio";

type Row = Record<string, any>;
const HUB = "/partner/research-hub";

export default function AnalystWorkbench() {
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading, error, refetch } = useCioThesisHealth();

  const rows: Row[] = Array.isArray(data) ? data.filter(Boolean) : [];

  function scrollToList() {
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  const entries: {
    title: string;
    description: string;
    icon: typeof Microscope;
    onClick: () => void;
  }[] = [
    {
      title: "Deep-dive a view",
      description: "Pick one of our views and read the case for and against.",
      icon: Microscope,
      onClick: scrollToList,
    },
    {
      title: "Trace a scenario",
      description: "Follow a what-if through the views it would touch.",
      icon: Route,
      onClick: () => navigate(`${HUB}/workbench/scenario`),
    },
    {
      title: "Coverage gaps",
      description: "See where the book is barely researched.",
      icon: ScanSearch,
      onClick: () => navigate(`${HUB}/workbench/coverage`),
    },
    {
      title: "Where we diverge",
      description: "Find the views of ours that pull against each other.",
      icon: GitBranch,
      onClick: () => navigate(`${HUB}/workbench/divergence`),
    },
  ];

  return (
    <div className="space-y-8">
      <header className="space-y-1">
        <h1 className="font-heading text-2xl font-semibold text-foreground">Workbench</h1>
        <p className="font-body text-sm text-muted-foreground">
          Pick a place to start. Each route opens a different way to interrogate the house view.
        </p>
      </header>

      {/* Entry cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {entries.map((e) => {
          const Icon = e.icon;
          return (
            <Card key={e.title} className="p-0 overflow-hidden">
              <button
                type="button"
                onClick={e.onClick}
                className="group flex h-full w-full flex-col items-start gap-2 p-4 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon className="h-5 w-5" aria-hidden />
                </span>
                <span className="font-heading text-base font-medium text-foreground">{e.title}</span>
                <span className="font-body text-sm text-muted-foreground">{e.description}</span>
              </button>
            </Card>
          );
        })}
      </div>

      {/* Quieter fifth link */}
      <button
        type="button"
        onClick={() => navigate(`${HUB}/workbench/cohort`)}
        className="group inline-flex items-center gap-1.5 font-body text-sm text-primary hover:underline"
      >
        <Users className="h-4 w-4" aria-hidden />
        Where managers disagree
        <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
      </button>

      {/* Pick a view to deep-dive */}
      <div ref={listRef} className="scroll-mt-6">
        <HubSection
          title="Pick a view to deep-dive"
          description="Open any view to read the evidence behind it."
        >
          {isLoading ? (
            <LoadingState rows={4} />
          ) : error ? (
            <ErrorState onRetry={() => refetch()} />
          ) : rows.length === 0 ? (
            <EmptyState title="No views to deep-dive yet." />
          ) : (
            <div className="space-y-2">
              {rows.map((r, i) => {
                const sentence = beliefSentence(r.from_label, r.to_label, r.thesis_direction);
                const hs = healthStatus(r.health_status);
                return (
                  <Card
                    key={r.edge_id ?? i}
                    role="button"
                    tabIndex={0}
                    onClick={() => r.edge_id && navigate(`${HUB}/thesis/${r.edge_id}`)}
                    onKeyDown={(ev) => {
                      if ((ev.key === "Enter" || ev.key === " ") && r.edge_id) {
                        ev.preventDefault();
                        navigate(`${HUB}/thesis/${r.edge_id}`);
                      }
                    }}
                    className="flex cursor-pointer items-center justify-between gap-3 p-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-body text-sm truncate">{sentence}</div>
                      <div className="mt-1">
                        <StrengthBar value={r.conviction_score} />
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <AttentionBadge label={hs.label} tone={hs.tone} />
                      <ChevronRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </HubSection>
      </div>
    </div>
  );
}
